/**
 * What to show in a case's Hearing Type field.
 *
 * The old system had ONE type value per case; the import mapped it BY NAME into Algovi's Case
 * Type (117,157 of 117,158 imported cases), leaving Hearing Type empty because Algovi splits that
 * single legacy field in two. So for IMPORTED cases the legacy value already exists on the case —
 * this surfaces it in the Hearing Type spot the coordinators actually look at, tagged so it can't
 * be mistaken for a real Algovi hearing type.
 *
 * Imported cases are recognisable by their case_id: the import stored the raw legacy number
 * ('395733'), while cases created in Algovi get 'c####'. New cases therefore NEVER hit the
 * fallback — this display rule retires by itself as legacy cases age out, exactly the "temporary
 * problem" the client described. Billing/validation is untouched: hearing_type_id stays null.
 */

export const isImportedCase = (courtCase) =>
  !!courtCase?.case_id && !String(courtCase.case_id).startsWith('c');

/**
 * Plain-text label (used where the cell renders a string).
 * `tag: false` drops the "(from old system)" suffix — interpreter-facing surfaces show just the
 * value (client request); the tag is an admin-side hint that the real hearing type is still unset.
 */
export const hearingTypeLabel = (courtCase, { tag = true } = {}) => {
  if (courtCase?.hearing_type?.name) return courtCase.hearing_type.name;
  if (isImportedCase(courtCase) && courtCase?.case_type?.name) {
    return tag ? `${courtCase.case_type.name} (from old system)` : courtCase.case_type.name;
  }
  return '-';
};

/** The legacy value alone, or null — for callers that want to style the tag themselves. */
export const legacyHearingValue = (courtCase) => {
  if (courtCase?.hearing_type?.name) return null;
  return isImportedCase(courtCase) && courtCase?.case_type?.name ? courtCase.case_type.name : null;
};
